import "./Card.css";
import { Link } from "react-router-dom";
import SaveButton from "./SaveButton";
import { datesLocales } from "../utils/heureLocale";

// Toutes ces dates sont formatées en UTC : datesLocales() range l'heure de
// Paris dans les composantes UTC. Les formater dans le fuseau du navigateur
// rejouerait la conversion fautive qu'on vient de défaire.
const weekdayShort = new Intl.DateTimeFormat("fr-FR", { weekday: "short", timeZone: "UTC" });
const dayMonth = new Intl.DateTimeFormat("fr-FR", {
  day: "numeric",
  month: "short",
  timeZone: "UTC",
});
const dayMonthYear = new Intl.DateTimeFormat("fr-FR", {
  day: "numeric",
  month: "short",
  year: "numeric",
  timeZone: "UTC",
});

const UNE_OCCURRENCE_MS = 24 * 60 * 60 * 1000;

function startOfDay(date) {
  return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

// "20h00", ou "" pour un événement à la journée.
function hourLabel(date) {
  const h = date.getUTCHours();
  const m = date.getUTCMinutes();
  if (h === 0 && m === 0) return "";
  return `${String(h).padStart(2, "0")}h${String(m).padStart(2, "0")}`;
}

// Surtitre compact d'une carte : "Mar. 20 oct. · 20h00", "20 et 21 oct.",
// "Du 20 oct. au 31 déc.".
export function formatCardDate(startStr, endStr) {
  const { debut: start, fin: end, journeesEntieres } = datesLocales(startStr, endStr);
  if (!start) return "";
  const endValid = !!end;

  // Une soirée de 21h à 2h tient en une occurrence : on l'annonce par son
  // jour et son heure de début, pas comme « 22 et 23 oct. ». Mais seulement
  // si elle finit au petit matin : deux séances, mercredi 14h30 et jeudi
  // 11h, tiennent aussi en moins de 24 h et restent bien deux jours.
  const finitDansLaNuit = endValid && end.getUTCHours() < 6;
  const uneOccurrence =
    !endValid ||
    startOfDay(end) === startOfDay(start) ||
    (!journeesEntieres && finitDansLaNuit && end - start < UNE_OCCURRENCE_MS);

  if (uneOccurrence) {
    const day = `${weekdayShort.format(start)} ${dayMonth.format(start)}`;
    const hour = journeesEntieres ? "" : hourLabel(start);
    return hour ? `${day} · ${hour}` : day;
  }

  const dayDiff = Math.round((startOfDay(end) - startOfDay(start)) / 86400000);
  if (dayDiff === 1) {
    return `${dayMonth.format(start)} et ${dayMonth.format(end)}`;
  }

  const endFormatter =
    end.getUTCFullYear() === start.getUTCFullYear() ? dayMonth : dayMonthYear;
  return `Du ${dayMonth.format(start)} au ${endFormatter.format(end)}`;
}

export function priceBadge(priceType) {
  if (priceType === "gratuit") {
    return { label: "Gratuit", variant: "pa-badge--free" };
  }
  if (priceType === "payant") {
    return { label: "Payant", variant: "pa-badge--paid" };
  }
  if (typeof priceType === "string" && priceType.startsWith("gratuit")) {
    return { label: "Gratuit sous condition", variant: "pa-badge--muted" };
  }
  return null;
}

function Card({ content }) {
  const f = content.fields;
  const date = formatCardDate(f.date_start, f.date_end);
  const price = priceBadge(f.price_type);

  return (
    <article className="pa-card">
      <Link
        to={`/event/${content.id}`}
        className="pa-card__media"
        tabIndex={-1}
        aria-hidden="true"
      >
        {f.cover_url ? (
          <img
            src={f.cover_url}
            className="pa-card__img"
            alt=""
            loading="lazy"
          />
        ) : (
          <span className="pa-card__noimg">
            <i className="bi bi-image" aria-hidden="true"></i>
          </span>
        )}
      </Link>

      {date && <p className="pa-card__date">{date}</p>}

      <h3 className="pa-card__title">
        <Link to={`/event/${content.id}`} className="stretched-link">
          {f.title}
        </Link>
      </h3>

      {f.address_name && (
        <p className="pa-card__venue">
          <i className="bi bi-geo-alt" aria-hidden="true"></i>
          <span>{f.address_name}</span>
        </p>
      )}

      <div className="pa-card__foot">
        {price ? (
          <span className={`pa-badge ${price.variant}`}>{price.label}</span>
        ) : (
          <span aria-hidden="true"></span>
        )}
        <div className="pa-above">
          <SaveButton id={content.id} variant="icon" title={f.title} />
        </div>
      </div>
    </article>
  );
}

export default Card;
